/**
 * Carousel lazy media loading context.
 *
 * Horizontal carousels (landing Explore section, Overview service strips) used to
 * mount every tile's <img> / <video> at once, triggering 50+ parallel fetches on
 * cold load. Browser `loading="lazy"` is tuned for vertical pages, not horizontal
 * strips, so it does not help here.
 *
 * This provider mounts a single IntersectionObserver rooted at the Mantine
 * ScrollArea viewport with a modest horizontal `rootMargin` so nearby tiles can
 * prefetch stills/posters. Public animated e-cards keep MP4 behind
 * {@link PublicBrowseVideoProvider} (PBI #1142) — this lazy gate only mounts
 * stills/posters; video activation (visibility / hover / play) is separate.
 * Each tile calls {@link useCarouselLazyMediaGate} to defer mounting still
 * media until observed (or immediately when marked eager).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

/**
 * Horizontal buffer as a percentage of the root viewport width (left and right).
 *
 * Kept intentionally modest so intersection mainly prefetches nearby stills/posters.
 * MP4 download still requires PublicBrowseVideoProvider activation (visibility
 * winner after poster-first delay, hover, or play) — not this lazy-media gate.
 */
const ROOT_MARGIN_HORIZONTAL_PERCENT = '50%';

type SubscribeFn = (element: HTMLElement, onVisible: () => void) => () => void;

const CarouselLazyMediaContext = createContext<SubscribeFn | null>(null);

interface CarouselLazyMediaProviderProps {
  /** Mantine ScrollArea viewport element — the IntersectionObserver root. */
  viewportRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

/**
 * Wraps carousel tile children. Must sit inside the same ScrollArea that owns
 * `viewportRef` so observed intersections use the horizontal scroll viewport as root.
 */
export const CarouselLazyMediaProvider = ({
  viewportRef,
  children,
}: CarouselLazyMediaProviderProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const handlersRef = useRef(new WeakMap<Element, () => void>());

  useLayoutEffect(() => {
    const root = viewportRef.current;
    if (!root) {
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }

          const handler = handlersRef.current.get(entry.target);
          handler?.();
          io.unobserve(entry.target);
          handlersRef.current.delete(entry.target);
        }
      },
      {
        root,
        rootMargin: `0px ${ROOT_MARGIN_HORIZONTAL_PERCENT} 0px ${ROOT_MARGIN_HORIZONTAL_PERCENT}`,
        threshold: 0,
      },
    );

    observerRef.current = io;

    return () => {
      io.disconnect();
      observerRef.current = null;
    };
  }, [viewportRef]);

  const subscribe = useCallback((element: HTMLElement, onVisible: () => void) => {
    const io = observerRef.current;
    if (!io) {
      onVisible();

      return () => {};
    }

    handlersRef.current.set(element, onVisible);
    io.observe(element);

    return () => {
      io.unobserve(element);
      handlersRef.current.delete(element);
    };
  }, []);

  return (
    <CarouselLazyMediaContext.Provider value={subscribe}>{children}</CarouselLazyMediaContext.Provider>
  );
};

interface UseCarouselLazyMediaGateOptions {
  /**
   * When true, the first tile in a carousel loads media immediately (hero tile).
   */
  eager: boolean;
  /**
   * When false, lazy gating is disabled and media always mounts (grids, modals, pickers).
   */
  enabled: boolean;
}

/**
 * Returns whether carousel media (image / video) should mount, and a ref to attach
 * to the tile root for intersection observation.
 *
 * When {@link enabled} is false, always loads. When enabled and not eager, defers
 * until the tile intersects the prefetch region; if no provider is mounted, loads
 * immediately so standalone tiles never stay blank.
 */
export const useCarouselLazyMediaGate = ({ eager, enabled }: UseCarouselLazyMediaGateOptions) => {
  const subscribe = useContext(CarouselLazyMediaContext);

  const shouldDefer = enabled && !eager && subscribe !== null;
  const [deferredLoaded, setDeferredLoaded] = useState(!shouldDefer);
  const tileRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled || eager) {
      setDeferredLoaded(true);
    }
  }, [eager, enabled]);

  useEffect(() => {
    if (!shouldDefer || deferredLoaded || !subscribe) {
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    let rafId = 0;

    const attach = () => {
      if (cancelled) {
        return;
      }

      const el = tileRootRef.current;
      if (!el) {
        // Parent effects can run before the tile root ref is committed.
        rafId = window.requestAnimationFrame(attach);
        return;
      }

      unsubscribe = subscribe(el, () => setDeferredLoaded(true));
    };

    attach();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      unsubscribe?.();
    };
  }, [shouldDefer, deferredLoaded, subscribe]);

  const shouldLoadMedia = !enabled || eager || deferredLoaded || subscribe === null;

  return { shouldLoadMedia, tileRootRef };
};
